import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  EmailVerificationWidget,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  IconArrowHead,
  OutsideClickHandler,
  IconCollection,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import { currencyData } from '../../../../config/configStripe';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentUserCurrency } from '../../../../ducks/user.duck';
import CountryFlag from '../../../../components/CountryFlag/CountryFlag';
import CartIcon from '../../../../components/CartIcon/CartIcon';
import { createResourceLocatorString } from '../../../../util/routes';
import { openStoreCartInURL } from '../../../../util/dateRangeHelpers';
import { getSearchWithStoreCart } from '../../../../util/dateRangeHelpers';
import css from './TopbarDesktop.module.css';
import Cookies from 'js-cookie';
import { isAuthenticatedSelector } from '../../../../ducks/auth.duck';
import { updateProfile } from '../../../ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  CURRENCY_COOKIE_KEY,
  isCurrentUserVerified,
} from '../../../../util/constants';
import LanguageSelector from '../../../../components/LanguageSelector/LanguageSelector';

const CONTACT_PHONE =
  process.env.REACT_APP_TEAM_PHONE_NUMBER || '1-833-234-RENT';

const SignupLink = () => {
  return (
    <NamedLink
      name="SignupPage"
      className={classNames(css.topbarLink, css.authLink)}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink
      name="LoginPage"
      className={classNames(css.topbarLink, css.authLink)}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot =
    notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.cart}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      {/* <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span> */}
      <CartIcon isInbox itemsCount={notificationCount} />
    </NamedLink>
  );
};

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showChangeProfileIcon,
  profilePageLinkProps,
  intl,
  onOpenVerificationModal,
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' &&
      ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage
      ? css.currentPage
      : null;
  };

  const isUserIdentityVerified = isCurrentUserVerified(currentUser);

  const { firstName } =
    (currentUser && currentUser.id && currentUser.attributes.profile) || {};

  return (
    <Menu>
      <MenuLabel
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
      >
        <Avatar
          className={css.avatar}
          user={currentUser}
          disableProfileLink
          showChangeProfileIcon={showChangeProfileIcon}
        />
        <p className={css.accountText}>
          {' '}
          {currentUser ? (
            firstName
          ) : (
            <FormattedMessage id="TopbarDesktop.account" />
          )}
        </p>
        <span className={css.profileMenuArrow}>
          <IconCollection name="topbar_down" />
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem className={css.profileMenuLink} key="ProfileEditPage">
          <div className={css.profileMenuLinkContent}>
            <div className={css.profileMenuLinkContentAvatar}>
              <NamedLink
                className={classNames(css.ProfilemenuLink)}
                {...profilePageLinkProps}
              >
                <Avatar
                  className={css.avatar}
                  user={currentUser}
                  disableProfileLink
                  showChangeProfileIcon={showChangeProfileIcon}
                />
                <span className={css.profileActive} />
                {/* <IconCollection name="profile_edit" /> */}
              </NamedLink>
            </div>
            <div>
              <p className={css.accountName}>
                {' '}
                {currentUser ? (
                  firstName
                ) : (
                  <FormattedMessage id="TopbarDesktop.account" />
                )}
              </p>
              <p className={css.accountEmail}>
                {' '}
                {currentUser?.attributes?.email}
              </p>
            </div>
          </div>
        </MenuItem>
        {isUserIdentityVerified === false && (
          <MenuItem key="verification" className={css.profileVerificationLink}>
            <InlineTextButton
              rootClassName={classNames(css.menuLink, css.verificationLink)}
              onClick={onOpenVerificationModal}
            >
              <span className={css.menuItemBorder} />
              <i className="ri-shield-line"></i>
              <FormattedMessage
                id="TopbarDesktop.verifyIdentity"
                defaultMessage="Verify Your Identity"
              />
            </InlineTextButton>
          </MenuItem>
        )}
        {/* <MenuItem key="PhoneNumber">
          <div className={css.PrimaryButtonContainer}>
            <a href={`tel:${CONTACT_PHONE}`} className={css.PrimaryButton}>{intl.formatMessage({ id: "Topbar.callUs" })} {CONTACT_PHONE}</a>
          </div>

        </MenuItem> */}

        <MenuItem key="ProfilePage">
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('ProfilePage')
            )}
            {...profilePageLinkProps}
          >
            <span className={css.menuItemBorder} />
            <i className="ri-store-2-line"></i>
            <FormattedMessage id="TopbarDesktop.profilePageLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('ManageListingsPage')
            )}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <i className="ri-list-check-2"></i>
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem
          key="ProfileSettingsPage"
          className={css.profileVerificationLink}
        >
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('ProfileSettingsPage')
            )}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <i className="ri-shield-line"></i>
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="StoreSettingsPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('StoreSettingsPage')
            )}
            name="StoreSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <i className="ri-store-2-line"></i>
            <FormattedMessage id="TopbarDesktop.storeSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem
          key="AccountSettingsPage"
          className={css.profileVerificationLink}
        >
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('AccountSettingsPage'),
              css.accountSettings // optional: for relative positioning
            )}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <i className="ri-user-settings-line"></i>
            <span className={css.tabWithBadge}>
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
              {isUserIdentityVerified === false ? (
                <span className={`${css.notificationDotTab} ${css.show}`}>
                  1
                </span>
              ) : null}
            </span>
          </NamedLink>
        </MenuItem>

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <i className="ri-logout-box-line"></i>
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const CurrencySelector = ({
  currencyOptions,
  onSelect,
  currentUserCurrency,
}) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const [selectedOption, setSelectedOption] = useState(currentUserCurrency);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const updatedOptions = currencyOptions.reduce((acc, option) => {
    if (option.code === currentUserCurrency) {
      acc.unshift(option);
    } else {
      acc.push(option);
    }
    return acc;
  }, []);

  const userInitialCurrencyData = currencyOptions.find(
    o => o.code === currentUserCurrency
  );
  useEffect(() => {
    setSelectedOption(userInitialCurrencyData);
  }, [userInitialCurrencyData]);

  const handleOptionClick = option => {
    const { code } = option || {};

    setSelectedOption(option);
    setIsDropdownOpen(false);
    onSelect(code);

    // Save to cookie
    Cookies.set(CURRENCY_COOKIE_KEY, code, {
      expires: 30,
      secure: true,
    });

    // Save to redux
    dispatch(setCurrentUserCurrency(code));

    // Save to profile
    if (isAuthenticated) {
      dispatch(updateProfile({ publicData: { currency: code } })).catch(() => {
        // Error is already handled via Redux state
        // This catch prevents unhandled promise rejection
      });
    }
  };

  return (
    <div className={css.dropdown}>
      <OutsideClickHandler onOutsideClick={() => setIsDropdownOpen(false)}>
        <div
          className={classNames(
            css.selectedOption,
            isDropdownOpen ? css.selectedOptionOpen : css.selectedOptionClose
          )}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <div className={css.userCurrency}>
            <span className={css.countryFlag}>
              <CountryFlag
                code={selectedOption.countryCode}
                height="20"
                width="20"
                alt={selectedOption.countryCode}
              />
            </span>
            <span>{selectedOption.code}</span>
          </div>
          <IconArrowHead
            direction="down"
            size="small"
            rootClassName={css.arrowIcon}
          />
        </div>
        {isDropdownOpen && (
          <div className={css.optionsContainer}>
            <div className={css.option}>
              <span>
                <FormattedMessage id="Topbar.currencyHeading" />
              </span>
            </div>
            {updatedOptions.map(option => (
              <div
                key={option.code}
                className={classNames(
                  selectedOption.code == option.code && css.activeOptions,
                  css.option
                )}
                onMouseDown={e => {
                  e.preventDefault(); // prevent blur/outside-click from firing before selection
                  handleOptionClick({
                    code: option.code,
                    countryCode: option.countryCode,
                  });
                }}
              >
                <span>
                  <CountryFlag
                    code={option.countryCode}
                    height="20"
                    width="20"
                    alt={option.countryCode}
                  />
                </span>
                <span>{option.code}</span>
              </div>
            ))}
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
};

const CartLink = ({ itemsCount }) => {
  const history = useHistory();
  const location = useLocation();
  const isStorefrontRoute = /\/rental-owner\/[^/]+/.test(location.pathname);

  if (isStorefrontRoute) {
    return (
      <InlineTextButton
        rootClassName={css.cart}
        onClick={() => openStoreCartInURL(history, location)}
      >
        <CartIcon itemsCount={itemsCount} />
        <span data-cart-link="true" />
      </InlineTextButton>
    );
  }

  return (
    <NamedLink name="CartPage" className={css.cart}>
      <CartIcon itemsCount={itemsCount} />
      <span data-cart-link="true" />
    </NamedLink>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentUserLocationInfo,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    onSearchFocus,
    initialSearchFormValues,
    currentUserCurrency,
    itemsCount,
    showChangeProfileIcon,
    profilePageLinkProps,
    notShowDesktopSearchBar,
    history,
    routeConfiguration,
    currentListing,
    pageAssetsData,
    pageId,
    onOpenVerificationModal,
  } = props;

  const [mounted, setMounted] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setMounted(true);
  }, []);

  const [showSearchBar, setShowSearchBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowSearchBar(window.scrollY >= 450);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const aboutLink = (
    <NamedLink
      name="CMSPage"
      className={css.topbarLink}
      params={{ pageId: 'about' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.about" />
      </span>
    </NamedLink>
  );

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <div className={css.profileMenuWrapper}>
      <ProfileMenu
        currentPage={currentPage}
        currentUser={currentUser}
        onLogout={onLogout}
        showChangeProfileIcon={showChangeProfileIcon}
        profilePageLinkProps={profilePageLinkProps}
        intl={intl}
        onOpenVerificationModal={onOpenVerificationModal}
      />
      <EmailVerificationWidget placement="desktop" />
    </div>
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage(
          { id: 'TopbarDesktop.logo' },
          { marketplaceName }
        )}
        // linkToExternalSite={config?.topbar?.logoLink}
      />
      {/* The search bar is only shown on non-search pages; the search page has its
          own sticky DesktopSearchBar so showing it here would cause confusion. */}
      <div
        className={classNames(
          css.searchWrapper,
          notShowDesktopSearchBar && css.activeSearch
        )}
        style={notShowDesktopSearchBar ? { display: 'none' } : undefined}
      >
        {!notShowDesktopSearchBar ? (
          <TopbarSearchForm
            className={classNames(css.searchLink, {
              [css.takeAvailableSpace]: giveSpaceForSearch,
            })}
            desktopInputRoot={css.topbarSearchWithLeftPadding}
            onSubmit={onSearchSubmit}
            initialValues={initialSearchFormValues}
            appConfig={config}
            // Focusing the topbar input is itself the "go to marketplace search" gesture
            // on desktop. Topbar owns what happens next so the store page can step in.
            onFocus={onSearchFocus}
          />
        ) : null}
      </div>

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={
          authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
        }
        aboutLink={aboutLink}
        cartLink={<CartLink intl={intl} itemsCount={itemsCount} />}
        profileMenuMaybe={profileMenuMaybe}
        inboxLinkMaybe={inboxLinkMaybe}
        currencySelector={
          <CurrencySelector
            currencyOptions={currencyData}
            onSelect={currency => dispatch(setCurrentUserCurrency(currency))}
            currentUserCurrency={currentUserCurrency}
          />
        }
        languageSelector={
          <LanguageSelector
            currentListing={currentListing}
            pageAssetsData={pageAssetsData}
            pageId={pageId}
            currentUserLocationInfo={currentUserLocationInfo}
          />
        }
        isPremiumMember={true}
      />

      {/* {inboxLinkMaybe} */}

      {signupLinkMaybe}
      {loginLinkMaybe}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  onSearchFocus: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
